import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import DataTable from '../components/DataTable';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import { cuentaContableService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';

const EMPTY = {
  codigo: '', descripcion: '',
  maneja_ruc: false, maneja_doc_ref1: false, maneja_doc_ref2: false, maneja_centro_costo: false,
};

const FLAGS = [
  { key: 'maneja_ruc',          label: 'RUC',                 desc: 'Requiere RUC al registrar movimientos' },
  { key: 'maneja_doc_ref1',     label: 'Documento Ref. 1',    desc: 'Tipo y número de documento principal' },
  { key: 'maneja_doc_ref2',     label: 'Documento Ref. 2',    desc: 'Tipo y número de documento secundario' },
  { key: 'maneja_centro_costo', label: 'Centro de Costo',     desc: 'Asignación de centro de costo' },
];

export default function CuentasContables() {
  const [records,  setRecords]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [alert,    setAlert]    = useState(null);
  const [toast,    setToast]    = useState(null);
  const [confirm,  setConfirm]  = useState(null);
  const [showModal,setShowModal]= useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await cuentaContableService.getAll();
      setRecords(r.data || []);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar cuentas contables.') });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowModal(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      codigo: r.codigo || '',
      descripcion: r.descripcion || '',
      maneja_ruc: !!r.maneja_ruc,
      maneja_doc_ref1: !!r.maneja_doc_ref1,
      maneja_doc_ref2: !!r.maneja_doc_ref2,
      maneja_centro_costo: !!r.maneja_centro_costo,
    });
    setShowModal(true);
  };

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggle = (k) => setForm(prev => ({ ...prev, [k]: !prev[k] }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.codigo.trim() || !form.descripcion.trim()) {
      setAlert({ type: 'warning', message: 'Código y descripción son obligatorios.' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await cuentaContableService.update(editing.id_cuenta_contable, form);
        setToast({ type: 'success', message: 'Cuenta contable actualizada.' });
      } else {
        await cuentaContableService.create(form);
        setToast({ type: 'success', message: 'Cuenta contable creada.' });
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    } finally { setSaving(false); }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Cuenta Contable',
      message: `¿Eliminar la cuenta "${r.codigo} — ${r.descripcion}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await cuentaContableService.delete(r.id_cuenta_contable);
          setToast({ type: 'success', message: 'Cuenta eliminada.' });
          fetchData();
        } catch (err) {
          setAlert({ type: 'error', message: getErrorDetail(err, 'Error al eliminar.') });
        }
      },
    });
  };

  const BadgeFlag = ({ on, children }) => (
    <span style={{
      display: 'inline-block', padding: '1px 7px', marginRight: 4, borderRadius: 4,
      fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
      background: on ? 'rgba(34,197,94,0.12)' : 'rgba(148,163,184,0.08)',
      color:      on ? '#22c55e' : '#64748b',
      border: `1px solid ${on ? 'rgba(34,197,94,0.3)' : 'rgba(148,163,184,0.18)'}`,
    }}>
      {on ? '✓' : '—'} {children}
    </span>
  );

  const columns = [
    { key: 'codigo',      label: 'Código',      width: 120,
      render: r => <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#93c5fd' }}>{r.codigo}</span> },
    { key: 'descripcion', label: 'Descripción' },
    { key: '_flags',      label: 'Detalle que maneja',
      render: r => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {FLAGS.map(f => <BadgeFlag key={f.key} on={r[f.key]}>{f.label}</BadgeFlag>)}
        </div>
      ) },
  ];

  return (
    <Layout title="Cuentas Contables" subtitle="Contabilidad">
      {alert   && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
      {toast   && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      {confirm && <Confirm {...confirm} onCancel={() => setConfirm(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Cuentas Contables</h2>
        <button className="btn btn-primary btn-sm" onClick={openNew}>+ Nueva Cuenta</button>
      </div>

      <DataTable
        columns={columns}
        data={records}
        idField="id_cuenta_contable"
        onEdit={openEdit}
        onDelete={handleDelete}
        loading={loading}
        emptyMessage="No hay cuentas contables registradas."
        exportFileName="cuentas_contables"
        hideToolbarRight
      />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Editar Cuenta Contable' : 'Nueva Cuenta Contable'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12 }}>
                  <div className="form-row" style={{ marginBottom: 0 }}>
                    <label>Código *</label>
                    <input value={form.codigo}
                      onChange={e => setF('codigo', e.target.value)}
                      placeholder="Ej: 60111"
                      style={{ fontFamily: 'Consolas, monospace', letterSpacing: 1, fontWeight: 600 }}
                      maxLength={20}
                      autoFocus required />
                  </div>
                  <div className="form-row" style={{ marginBottom: 0 }}>
                    <label>Descripción *</label>
                    <input value={form.descripcion}
                      onChange={e => setF('descripcion', e.target.value)}
                      placeholder="Ej: Mercaderías"
                      maxLength={150}
                      required />
                  </div>
                </div>

                <div style={{
                  border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
                  background: 'rgba(15,27,45,0.4)', padding: 14,
                }}>
                  <div style={{
                    fontSize: 10.5, color: '#64748b', textTransform: 'uppercase',
                    letterSpacing: 0.6, fontWeight: 700, marginBottom: 10,
                  }}>Detalle que maneja esta cuenta</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {FLAGS.map(f => {
                      const on = !!form[f.key];
                      return (
                        <button key={f.key} type="button" onClick={() => toggle(f.key)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 12px', borderRadius: 8, textAlign: 'left', cursor: 'pointer',
                            background: on ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${on ? 'rgba(34,197,94,0.35)' : 'rgba(255,255,255,0.08)'}`,
                            color: '#e2e8f0', transition: 'all 0.15s ease',
                          }}>
                          <div style={{
                            width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                            background: on ? '#22c55e' : 'rgba(255,255,255,0.05)',
                            border: `1.5px solid ${on ? '#22c55e' : 'rgba(255,255,255,0.15)'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {on && <span style={{ color: '#0f1b2d', fontWeight: 900, fontSize: 12 }}>✓</span>}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{f.label}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{f.desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
